import React from "react";
import { AgGrid, Flex, HelpIndicator, Text } from "@jsq/ds/core";
import { Icon } from "@jsq/ds/icons";
import { Sizes } from "@jsq/ds/styles";
import { MoneyCellRenderer } from "~/pages/Billing/components/MoneyCellRenderer";
import { DATE_FORMATS } from "~/utils/constants";
import { formatFullDateAndTimeWithLocalTimezone } from "~/utils/datetime";
import { CaseTypeDisplay } from "../../../CasesOverview/CaseDetailsDrawer/types";
import {
  BILLING_STATUS_FILTER_VALUES,
  ENTITY_STATUS_FILTER_VALUES,
  GRID_COLUMN_FILTER_DEBOUNCE_MS,
  PRODUCT_LINE_FILTER_VALUES,
} from "../../../constants";
import EntityStatusCellDropDownRenderer from "../EntityStatusCellDropDownRenderer";
import { entityNameCell } from "./styles_vanilla.css";
import type { BillingGroupEntityRow } from "./entitiesByBillingGroupDataSource";

const BillingGroupCellRenderer = (
  params: AgGrid.ICellRendererParams<BillingGroupEntityRow>,
): React.ReactNode => {
  const data = params.data;
  if (!data) {
    return null;
  }
  if (data.type === "billingGroup") {
    return data.billingGroupName ?? "";
  }
  return null;
};

const EntityCellRenderer = (
  params: AgGrid.ICellRendererParams<BillingGroupEntityRow>,
): React.ReactNode => {
  const data = params.data;
  if (!data || data.type !== "entity") {
    return null;
  }
  const showSpinner = data.entityData?.showSpinner;

  return (
    <Flex alignItems="center" gap={Sizes.SM} className={entityNameCell}>
      <Text>{data.entity ?? ""}</Text>
      {showSpinner && (
        <HelpIndicator
          id="entity-sync-tooltip"
          placement="top"
          content="Update in progress. The EUM calculation will be refreshed once the updates have been processed."
        >
          <Icon type="sync-alt" data-testid="icon-sync-alt" />
        </HelpIndicator>
      )}
    </Flex>
  );
};

const getEntityValue = <T,>(
  data: BillingGroupEntityRow | undefined,
  field: string,
): T | null | undefined => {
  if (!data || data.type !== "entity" || !data.entityData) {
    return undefined;
  }
  const entityData: Record<string, unknown> = data.entityData;
  return entityData[field] as T | null | undefined;
};

export type EntityStatusColumnParams = {
  updateLoading: boolean;
  onEntityStatusChange: (params: AgGrid.NewValueParams<BillingGroupEntityRow>) => void;
};

const createEntityStatusCellRenderer = (updateLoading: boolean) => {
  const EntityStatusCellRenderer = (
    params: AgGrid.ICellRendererParams<BillingGroupEntityRow>,
  ): React.ReactNode => {
    const data = params.data;
    if (!data || data.type !== "entity") {
      return null;
    }
    const isDropdownEnabled = !(updateLoading || Boolean(data.entityData?.showSpinner));
    if (isDropdownEnabled) {
      return <EntityStatusCellDropDownRenderer params={params} />;
    }
    return <Text>{params.value}</Text>;
  };
  return EntityStatusCellRenderer;
};

const createIsEntityStatusEditable = (updateLoading: boolean) => {
  return (params: AgGrid.EditableCallbackParams<BillingGroupEntityRow>): boolean => {
    const data = params.data;
    if (!data || data.type !== "entity") {
      return false;
    }
    return !(updateLoading || Boolean(data.entityData?.showSpinner));
  };
};

type EntityStatus = NonNullable<BillingGroupEntityRow["entityData"]>["entityStatus"];

const isEntityStatus = (value: unknown): value is EntityStatus => {
  return typeof value === "string" && ENTITY_STATUS_FILTER_VALUES.includes(value);
};

const entityStatusValueSetter = (
  params: AgGrid.ValueSetterParams<BillingGroupEntityRow>,
): boolean => {
  const data = params.data;
  if (!data || data.type !== "entity" || !data.entityData) {
    return false;
  }
  const newValue: unknown = params.newValue;
  if (!isEntityStatus(newValue)) {
    return false;
  }
  if (data.entityData.entityStatus === newValue) {
    return false;
  }
  data.entityData.entityStatus = newValue;
  return true;
};

export const formatFirstCapitalCallDate = (
  params: AgGrid.ValueFormatterParams<BillingGroupEntityRow>,
): string => {
  if (!params.value) return "";
  try {
    return formatFullDateAndTimeWithLocalTimezone(
      params.value,
      DATE_FORMATS["MM/DD/YYYY"].long,
    );
  } catch {
    return params.value;
  }
};

export const getBillingGroupColumnDefs = (
  entityStatusParams?: EntityStatusColumnParams,
  isFCCEnabled = false,
  isSafeCaseIdAndActiveInvestmentsEnabled = false,
): (AgGrid.ColDef<BillingGroupEntityRow> | AgGrid.ColGroupDef<BillingGroupEntityRow>)[] => [
  {
    field: "billingGroupId",
    headerName: "Billing Group",
    rowGroup: true,
    hide: true,
    suppressColumnsToolPanel: true,
    filter: "agTextColumnFilter",
    filterParams: {
      filterOptions: ["contains"],
      maxNumConditions: 1,
      debounceMs: GRID_COLUMN_FILTER_DEBOUNCE_MS,
    },
  },
  {
    colId: "entity",
    headerName: "Entity",
    headerTooltip: "JSQ Entity Name",
    minWidth: 260,
    width: 260,
    filter: "agTextColumnFilter",
    filterParams: {
      filterOptions: ["contains"],
      maxNumConditions: 1,
      debounceMs: GRID_COLUMN_FILTER_DEBOUNCE_MS,
    },
    cellRenderer: EntityCellRenderer,
    valueGetter: (params) => (params.data?.type === "entity" ? params.data.entity : null),
    pinned: "left",
    hide: false,
  },
  {
    colId: "customerLegalName",
    headerName: "Customer Legal Name",
    headerTooltip: "Customer entity Legal Name",
    minWidth: 200,
    width: 200,
    filter: "agTextColumnFilter",
    filterParams: {
      filterOptions: ["contains"],
      maxNumConditions: 1,
      debounceMs: GRID_COLUMN_FILTER_DEBOUNCE_MS,
    },
    valueGetter: (params) => getEntityValue<string>(params.data, "customerLegalName"),
    hide: true,
  },
  {
    field: "entityId",
    headerName: "Entity ID",
    headerTooltip: "Global ID for clients across internal systems. Set by BizOps.",
    minWidth: 120,
    width: 120,
    filter: "agNumberColumnFilter",
    filterParams: {
      filterOptions: ["equals"],
      maxNumConditions: 1,
      debounceMs: GRID_COLUMN_FILTER_DEBOUNCE_MS,
    },
    cellStyle: { textAlign: "right" },
    hide: true,
  },
  {
    colId: "sfdcEntityId",
    headerName: "SFDC Entity ID",
    headerTooltip: "Salesforce Entity ID",
    minWidth: 150,
    width: 150,
    filter: "agTextColumnFilter",
    filterParams: {
      filterOptions: ["contains"],
      maxNumConditions: 1,
      debounceMs: GRID_COLUMN_FILTER_DEBOUNCE_MS,
    },
    valueGetter: (params) => getEntityValue<string>(params.data, "sfdcEntityId"),
    hide: false,
  },
  ...(isSafeCaseIdAndActiveInvestmentsEnabled
    ? [
        {
          colId: "safeCaseId",
          headerName: "Safe Case ID",
          headerTooltip: "Salesforce Safe Case ID",
          minWidth: 150,
          width: 150,
          valueGetter: (params: AgGrid.ValueGetterParams<BillingGroupEntityRow>) =>
            getEntityValue<string>(params.data, "safeCaseId"),
          hide: false,
        },
      ]
    : []),
  {
    colId: "faEntityNumber",
    headerName: "FA Entity Number",
    headerTooltip: "Fund Admin Entity Number",
    minWidth: 150,
    width: 150,
    valueGetter: (params: AgGrid.ValueGetterParams<BillingGroupEntityRow>) =>
      getEntityValue<number>(params.data, "faEntityNumber"),
    cellStyle: { textAlign: "right" },
    hide: false,
  },
  {
    colId: "jsqProductLine",
    headerName: "JSQ product line",
    headerTooltip: "JSQ Product Line",
    minWidth: 150,
    width: 150,
    filter: "agSetColumnFilter",
    filterParams: {
      debounceMs: GRID_COLUMN_FILTER_DEBOUNCE_MS,
      values: PRODUCT_LINE_FILTER_VALUES,
    },
    valueGetter: (params) => getEntityValue<string>(params.data, "jsqProductLine"),
    hide: false,
  },
  {
    colId: "billingStatus",
    headerName: "Billing status",
    headerTooltip: 'Entity billing status: "Billable" vs "Non-billable".',
    minWidth: 120,
    width: 120,
    filter: "agSetColumnFilter",
    filterParams: {
      debounceMs: GRID_COLUMN_FILTER_DEBOUNCE_MS,
      values: BILLING_STATUS_FILTER_VALUES,
    },
    valueGetter: (params) => getEntityValue<string>(params.data, "billingStatus"),
    hide: false,
  },
  {
    colId: "entityStatus",
    headerName: "Entity status",
    headerTooltip: 'Entity status: "Active" vs "Complete"',
    minWidth: 120,
    width: 120,
    filter: "agSetColumnFilter",
    filterParams: {
      debounceMs: GRID_COLUMN_FILTER_DEBOUNCE_MS,
      values: ENTITY_STATUS_FILTER_VALUES,
    },
    valueGetter: (params) => getEntityValue<string>(params.data, "entityStatus"),
    hide: false,
    ...(entityStatusParams && {
      cellRenderer: createEntityStatusCellRenderer(entityStatusParams.updateLoading),
      cellEditor: "agRichSelectCellEditor",
      cellEditorParams: {
        values: ["Active", "Completed"],
        cellHeight: 40,
      },
      editable: createIsEntityStatusEditable(entityStatusParams.updateLoading),
      valueSetter: entityStatusValueSetter,
      singleClickEdit: true,
      cellStyle: { alignContent: "center" },
      onCellValueChanged: entityStatusParams.onEntityStatusChange,
    }),
  },
  {
    colId: "faEntityType",
    headerName: "FA Entity Type",
    headerTooltip: "Indicates the type of fund (Main Fund, Feeder, HoldCo, Master, etc)",
    minWidth: 150,
    width: 150,
    filter: "agTextColumnFilter",
    filterParams: {
      filterOptions: ["contains"],
      maxNumConditions: 1,
      debounceMs: GRID_COLUMN_FILTER_DEBOUNCE_MS,
    },
    valueGetter: (params) => getEntityValue<string>(params.data, "faEntityType"),
    hide: true,
  },
  {
    colId: "faEumType",
    headerName: "FA EUM Type",
    headerTooltip: "EUM calculation type per FA contract. Drives FA EUM calculation.",
    minWidth: 150,
    width: 150,
    filter: "agTextColumnFilter",
    filterParams: {
      filterOptions: ["contains"],
      maxNumConditions: 1,
      debounceMs: GRID_COLUMN_FILTER_DEBOUNCE_MS,
    },
    valueGetter: (params) => getEntityValue<string>(params.data, "faEumType"),
    hide: true,
  },
  {
    colId: "faFundFamily",
    headerName: "FA Fund Family",
    headerTooltip: "Family of fund entities which are related together.",
    minWidth: 150,
    width: 150,
    filter: "agTextColumnFilter",
    filterParams: {
      filterOptions: ["contains"],
      maxNumConditions: 1,
      debounceMs: GRID_COLUMN_FILTER_DEBOUNCE_MS,
    },
    valueGetter: (params) => getEntityValue<string>(params.data, "faFundFamily"),
    hide: true,
  },
  {
    colId: "serviceStartDate",
    headerName: "Service Start Date",
    headerTooltip: "Date that services and billing start.",
    minWidth: 150,
    width: 150,
    valueGetter: (params) => getEntityValue<string>(params.data, "serviceStartDate"),
    hide: true,
  },
  {
    colId: "serviceEndDate",
    headerName: "Service End Date",
    headerTooltip: "Date that services and billing end.",
    minWidth: 150,
    width: 150,
    valueGetter: (params) => getEntityValue<string>(params.data, "serviceEndDate"),
    hide: true,
  },
  ...(isFCCEnabled
    ? [
        {
          colId: "firstCapitalCallDate",
          headerName: CaseTypeDisplay.FirstCapitalCallDate,
          headerTooltip: `${CaseTypeDisplay.FirstCapitalCallDate} for the entity.`,
          minWidth: 180,
          width: 180,
          sortable: true,
          valueGetter: (params: AgGrid.ValueGetterParams<BillingGroupEntityRow>) =>
            getEntityValue<string>(params.data, "firstCapitalCallDate"),
          valueFormatter: formatFirstCapitalCallDate,
          hide: false,
        },
      ]
    : []),
  ...(isSafeCaseIdAndActiveInvestmentsEnabled
    ? [
        {
          colId: "feeBasis",
          headerName: "Fee Basis",
          headerTooltip: "Fee basis used to calculate fees for the entity.",
          minWidth: 180,
          width: 200,
          sortable: true,
          filter: "agTextColumnFilter",
          filterParams: {
            filterOptions: ["contains"],
            maxNumConditions: 1,
            debounceMs: GRID_COLUMN_FILTER_DEBOUNCE_MS,
          },
          valueGetter: (params: AgGrid.ValueGetterParams<BillingGroupEntityRow>) =>
            getEntityValue<string>(params.data, "feeBasis"),
          valueFormatter: (
            params: AgGrid.ValueFormatterParams<
              BillingGroupEntityRow,
              string | null | undefined
            >,
          ) => (params.value == null ? "--" : params.value),
          hide: false,
        },
        {
          colId: "activeInvestments",
          headerName: "Active Investments",
          headerTooltip:
            "Count of distinct active GL deals — per entity, with billing-group rows showing the total across entities in the group.",
          minWidth: 180,
          width: 180,
          sortable: true,
          filter: false,
          cellStyle: { textAlign: "right" },
          valueGetter: (params: AgGrid.ValueGetterParams<BillingGroupEntityRow>) => {
            if (params.data?.type === "billingGroup") {
              return params.data.activeInvestments;
            }
            return getEntityValue<number>(params.data, "activeInvestments");
          },
          valueFormatter: (
            params: AgGrid.ValueFormatterParams<BillingGroupEntityRow, number | null>,
          ) => (params.value == null ? "--" : String(params.value)),
          hide: false,
        },
      ]
    : []),
  {
    colId: "currency",
    headerName: "Currency",
    headerTooltip: "Currency setting for the entity",
    minWidth: 100,
    width: 100,
    filter: "agTextColumnFilter",
    filterParams: {
      filterOptions: ["contains"],
      maxNumConditions: 1,
      debounceMs: GRID_COLUMN_FILTER_DEBOUNCE_MS,
    },
    valueGetter: (params) => getEntityValue<string>(params.data, "currency"),
    hide: false,
  },
  {
    colId: "fxRate",
    headerName: "FX Rate",
    headerTooltip: "Local currency to USD FX rate for the day",
    minWidth: 100,
    width: 100,
    cellStyle: { textAlign: "right" },
    valueGetter: (params) => getEntityValue<number>(params.data, "fxRate"),
    hide: true,
  },
  {
    colId: "positionCountOfActiveInvestors",
    headerName: "Active Investors Count",
    headerTooltip:
      "Count of positions, excluding positions: with entity relationship, Closed positions",
    minWidth: 180,
    width: 180,
    cellStyle: { textAlign: "right" },
    valueGetter: (params) =>
      getEntityValue<number>(params.data, "positionCountOfActiveInvestors"),
    hide: false,
  },
  {
    colId: "commitmentsLocal",
    headerName: "Commitments (Local)",
    headerTooltip:
      "Aggregate of commitments in local currency for the entity (value seen in workspace)",
    minWidth: 170,
    width: 170,
    cellRenderer: MoneyCellRenderer,
    cellRendererParams: {
      currencyField: "currency",
      rounded: false,
    },
    cellStyle: { textAlign: "right" },
    valueGetter: (params) => getEntityValue<number>(params.data, "commitmentsLocal"),
    hide: true,
  },
  {
    colId: "commitmentsUsd",
    headerName: "Commitments (USD)",
    headerTooltip: "Commitments FX'd to USD",
    minWidth: 170,
    width: 170,
    cellRenderer: MoneyCellRenderer,
    cellRendererParams: {
      rounded: false,
    },
    cellStyle: { textAlign: "right" },
    valueGetter: (params) => getEntityValue<number>(params.data, "commitmentsUsd"),
    hide: true,
  },
  {
    colId: "contributionsLocal",
    headerName: "Contributions (Local)",
    headerTooltip:
      "Aggregate of commitments in local currency for the entity (value seen in workspace)",
    minWidth: 170,
    width: 170,
    cellRenderer: MoneyCellRenderer,
    cellRendererParams: {
      currencyField: "currency",
      rounded: false,
    },
    cellStyle: { textAlign: "right" },
    valueGetter: (params) => getEntityValue<number>(params.data, "contributionsLocal"),
    hide: true,
  },
  {
    colId: "contributionsUsd",
    headerName: "Contributions (USD)",
    headerTooltip: "Contributions FX'd to USD",
    minWidth: 170,
    width: 170,
    cellRenderer: MoneyCellRenderer,
    cellRendererParams: {
      rounded: false,
    },
    cellStyle: { textAlign: "right" },
    valueGetter: (params) => getEntityValue<number>(params.data, "contributionsUsd"),
    hide: true,
  },
  {
    colId: "redemptionsLocal",
    headerName: "Redemptions (Local)",
    headerTooltip:
      "Aggregate of redemptions in local currency for the entity (value seen in workspace)",
    minWidth: 170,
    width: 170,
    cellRenderer: MoneyCellRenderer,
    cellRendererParams: {
      currencyField: "currency",
      rounded: false,
    },
    cellStyle: { textAlign: "right" },
    valueGetter: (params) => getEntityValue<number>(params.data, "redemptionsLocal"),
    hide: true,
  },
  {
    colId: "redemptionsUsd",
    headerName: "Redemptions (USD)",
    headerTooltip: "Redemptions FX'd to USD",
    minWidth: 170,
    width: 170,
    cellRenderer: MoneyCellRenderer,
    cellRendererParams: {
      rounded: false,
    },
    cellStyle: { textAlign: "right" },
    valueGetter: (params) => getEntityValue<number>(params.data, "redemptionsUsd"),
    hide: true,
  },
  {
    colId: "distributionsLocal",
    headerName: "Distributions (Local)",
    headerTooltip:
      "Aggregate of distributions in local currency for the entity (value seen in workspace)",
    minWidth: 170,
    width: 170,
    cellRenderer: MoneyCellRenderer,
    cellRendererParams: {
      currencyField: "currency",
      rounded: false,
    },
    cellStyle: { textAlign: "right" },
    valueGetter: (params) => getEntityValue<number>(params.data, "distributionsLocal"),
    hide: true,
  },
  {
    colId: "distributionsUsd",
    headerName: "Distributions (USD)",
    headerTooltip: "Distributions FX'd to USD",
    minWidth: 170,
    width: 170,
    cellRenderer: MoneyCellRenderer,
    cellRendererParams: {
      rounded: false,
    },
    cellStyle: { textAlign: "right" },
    valueGetter: (params) => getEntityValue<number>(params.data, "distributionsUsd"),
    hide: true,
  },
  {
    colId: "currentBlendSoftwareEumLocal",
    headerName: "Software EUM (Local)",
    headerTooltip:
      "Total Sofware EUM in the client's local currency, inclusive of: entity is marked as complete or test, Positions marked as complete, Positions with entity ownership",
    minWidth: 250,
    width: 250,
    cellRenderer: MoneyCellRenderer,
    cellRendererParams: {
      currencyField: "currency",
      rounded: false,
    },
    cellStyle: { textAlign: "right" },
    valueGetter: (params) =>
      getEntityValue<number>(params.data, "currentBlendSoftwareEumLocal"),
    hide: true,
  },
  {
    colId: "currentBlendSoftwareEumUsd",
    headerName: "Software EUM (USD)",
    headerTooltip: "Total USD Software EUM (fx'd)",
    minWidth: 250,
    width: 250,
    cellRenderer: MoneyCellRenderer,
    cellRendererParams: {
      rounded: false,
    },
    cellStyle: { textAlign: "right" },
    valueGetter: (params) => {
      if (params.data?.type === "billingGroup") {
        return params.data.softwareEumUsd;
      }
      return getEntityValue<number>(params.data, "currentBlendSoftwareEumUsd");
    },
    hide: false,
  },
  {
    colId: "billableFaEumLocal",
    headerName: "Billable FA EUM (Local)",
    headerTooltip:
      "EUM used for billing of FA services in local currency. Depends on the EUM type selected.",
    minWidth: 200,
    width: 200,
    cellRenderer: MoneyCellRenderer,
    cellRendererParams: {
      currencyField: "currency",
      rounded: false,
    },
    cellStyle: { textAlign: "right" },
    valueGetter: (params) => getEntityValue<number>(params.data, "billableFaEumLocal"),
    hide: true,
  },
  {
    colId: "billableFaEumUsd",
    headerName: "Billable FA EUM (USD)",
    headerTooltip:
      "EUM used for billing of FA services in USD. Depends on the EUM type selected.",
    minWidth: 200,
    width: 200,
    cellRenderer: MoneyCellRenderer,
    cellRendererParams: {
      rounded: false,
    },
    cellStyle: { textAlign: "right" },
    valueGetter: (params) => {
      if (params.data?.type === "billingGroup") {
        return params.data.billableFaEumUsd;
      }
      return getEntityValue<number>(params.data, "billableFaEumUsd");
    },
    hide: false,
  },
  {
    colId: "discountedEumUsd",
    headerName: "Discounted EUM value",
    headerTooltip: "EUM override value applied for this entity. Null when no override exists.",
    minWidth: 200,
    width: 200,
    cellRenderer: MoneyCellRenderer,
    cellRendererParams: {
      rounded: false,
    },
    cellStyle: { textAlign: "right" },
    valueGetter: (params) => getEntityValue<number>(params.data, "discountedEumUsd"),
    hide: true,
  },
  {
    colId: "flatFeeRate",
    headerName: "Flat Fee Rate",
    headerTooltip: "The flat fee",
    minWidth: 120,
    width: 120,
    cellStyle: { textAlign: "right" },
    valueGetter: (params) => getEntityValue<number>(params.data, "flatFeeRate"),
    hide: false,
  },
  {
    colId: "countWirePayments",
    headerName: "Count of Wire payments",
    headerTooltip: "Count of wire payments for the period (month to date)",
    minWidth: 150,
    width: 150,
    cellStyle: { textAlign: "right" },
    valueGetter: (params) => getEntityValue<number>(params.data, "countWirePayments"),
    hide: false,
  },
  {
    colId: "countAchPayments",
    headerName: "Count of ACH payments",
    headerTooltip: "Count of ACH payments for the period (month to date)",
    minWidth: 150,
    width: 150,
    cellStyle: { textAlign: "right" },
    valueGetter: (params) => getEntityValue<number>(params.data, "countAchPayments"),
    hide: false,
  },
  {
    colId: "countCheckPayments",
    headerName: "Count of Check payments",
    headerTooltip: "Count of Check payments for the period (month to date)",
    minWidth: 150,
    width: 150,
    cellStyle: { textAlign: "right" },
    valueGetter: (params) => getEntityValue<number>(params.data, "countCheckPayments"),
    hide: false,
  },
  {
    colId: "returnOfCapitalLocal",
    headerName: "Return of Capital Distribution Amount (Local)",
    headerTooltip: "Return of Capital Distribution Amount",
    minWidth: 170,
    width: 170,
    cellRenderer: MoneyCellRenderer,
    cellRendererParams: {
      currencyField: "currency",
      rounded: false,
    },
    cellStyle: { textAlign: "right" },
    valueGetter: (params) => getEntityValue<number>(params.data, "returnOfCapitalLocal"),
    hide: true,
  },
  {
    colId: "returnOfCapitalUsd",
    headerName: "Return of Capital Distribution Amount (USD)",
    headerTooltip: "Return of Capital Distribution Amount FX'd to USD",
    minWidth: 170,
    width: 170,
    cellRenderer: MoneyCellRenderer,
    cellRendererParams: {
      rounded: false,
    },
    cellStyle: { textAlign: "right" },
    valueGetter: (params) => getEntityValue<number>(params.data, "returnOfCapitalUsd"),
    hide: true,
  },
];

export const billingGroupAutoGroupColumnDef: AgGrid.ColDef<BillingGroupEntityRow> = {
  headerName: "Billing group",
  headerTooltip: "Billing Group Name",
  minWidth: 180,
  width: 180,
  sortable: true,
  filter: "agGroupColumnFilter",
  menuTabs: ["generalMenuTab", "filterMenuTab"],
  mainMenuItems: (params) => {
    const defaultItems = params.defaultItems ?? [];
    return defaultItems.filter((item) => item !== "rowUnGroup");
  },
  cellRendererParams: {
    innerRenderer: BillingGroupCellRenderer,
    suppressCount: true,
  },
  pinned: "left",
  hide: false,
};
